import './index.scss'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import VolunteeringController from '@data/controller/VolunteeringController'

import closeIcon from 'url:@icon/close.svg';

export type RemoveVolunteerTarget = {
  personID: string;
  personName: string;
  teamID: string;
  teamName: string;
};

export default function RemoveVolunteerModal(props: {
  isOpen: boolean,
  target?: RemoveVolunteerTarget | null,
  close: () => void,
  onRemoved: () => void
}) {
  const auth = useAuth();
  const { t } = useTranslation(['removeVolunteer', 'volunteering']);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const removeVolunteer = async () => {
    if (!auth.churchId || !props.target) {
      setError(t('volunteering:errors.missingChurch'));
      return;
    }

    setIsRemoving(true);
    setError(null);

    try {
      const response = await VolunteeringController.removeTeamMember(
        auth.churchId,
        props.target.teamID,
        props.target.personID,
        auth
      );

      if (response.error) {
        setError(response.error);
        setIsRemoving(false);
        return;
      }

      props.onRemoved();
    } finally {
      setIsRemoving(false);
    }
  };

  return <Modal
    id='remove-volunteer-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='remove-volunteer-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isRemoving}
          onClick={props.close}>
          <img src={closeIcon} alt={t('volunteering:common.close')} />
        </button>

        <h3 className='title'>{t('title')}</h3>

        <span className='header-control-spacer' />
      </div>

      <div className='body'>
        {error ? <p className='save-error'>{error}</p> : null}

        <p className='remove-copy'>
          <strong>{props.target?.personName && props.target?.teamName ?
            t('confirm', { person: props.target.personName, team: props.target.teamName }) :
            t('confirmUnnamed')}</strong>
        </p>

        <p className='caption'>{t('caption')}</p>

        <div className='remove-actions'>
          <button
            type='button'
            className='remove-button'
            disabled={isRemoving}
            onClick={() => {
              void removeVolunteer();
            }}>
            {isRemoving ? t('removing') : t('remove')}
          </button>
        </div>
      </div>
    </div>
  </Modal>
}
